<script>
import {
  CdrButton, CdrInput, CdrLink, CdrList, CdrText, IconXFill,
} from '@rei/cedar';
import reiConst from '@rei/constants';
import EventItem from './eventItem.vue';
import postData from '../../utils/postData';
import metrics from '../../../mixins/metrics';

export default {
  name: 'NotifyMeModal',
  components: {
    CdrButton,
    CdrInput,
    CdrLink,
    CdrList,
    CdrText,
    IconXFill,
    EventItem,
  },
  mixins: [metrics],
  props: {
    eventData: {
      type: Object,
      required: true,
    },
    apiUrl: {
      type: String,
      default: '/events/event-order/people/waitlist',
    },
  },
  data() {
    return {
      firstName: null,
      lastName: null,
      email: null,
      status: null,
      statuses: {
        COMPLETE: 'COMPLETE',
        LOADING: 'LOADING',
        ERROR: 'ERROR',
      },
      formSubmitted: false,
      newTitle: "You're on the waitlist!",
    };
  },
  computed: {
    isFirstNameValid() {
      return (
        this.firstName !== null
        && this.firstName.length > 0
        && reiConst.VALIDATION_PATTERNS.FIRSTNAME.test(this.firstName)
      );
    },
    isLastNameValid() {
      return (
        this.lastName !== null
        && this.lastName.length > 0
        && reiConst.VALIDATION_PATTERNS.LASTNAME.test(this.lastName)
      );
    },
    isEmailValid() {
      return (
        this.email !== null
        && reiConst.VALIDATION_PATTERNS.EMAIL.test(this.email)
      );
    },
  },
  methods: {
    submitForm(e) {
      e.preventDefault();
      this.formSubmitted = true;
      if (this.status !== this.statuses.LOADING) {
        if (
          this.isFirstNameValid
          && this.isLastNameValid
          && this.isEmailValid
        ) {
          this.firstName = e.target.elements.firstName.value;
          this.lastName = e.target.elements.lastName.value;
          this.email = e.target.elements.email.value;
          this.eventId = e.target.elements.eventId.value;
          this.status = this.statuses.LOADING;
          // Request client side component html when location is updated
          postData(this.apiUrl, {
            firstName: this.firstName,
            lastName: this.lastName,
            email: this.email,
            eventId: this.eventId,
          })
            .then(() => {
              this.status = this.statuses.COMPLETE;
              this.metricsClick({ linkName: '{{ pageName }}_notify me modal_submit' });
              this.$emit('update-title', this.newTitle);
            })
            .catch(() => {
              this.status = this.statuses.ERROR;
            });
        } else {
          this.status = this.statuses.ERROR;
        }
      }
    },
    closeModal() {
      this.$emit('close-modal');
    },
    reset() {
      this.firstName = null;
      this.lastName = null;
      this.email = null;
      this.formSubmitted = false;
      this.status = null;
    },
  },
};
</script>

<template>
  <div class="notify-me-modal__content">
    <div v-if="status !== statuses.COMPLETE">
      <cdr-text
        id="notify-me-description"
        class="notify-me-modal__description"
      >
        <span class="notify-me-modal__sr-only">Fill out the form below, to submit your name and email as</span>
        <span>This event is currently full, but we can let you know if a spot opens up.</span>
      </cdr-text>
      <event-item v-bind="eventData" />
      <form
        class="modal-form"
        novalidate
        @submit="submitForm"
      >
        <fieldset>
          <legend class="notify-me-modal__sr-only">
            How to reach you
          </legend>
          <div class="modal-form__names">
            <div class="modal-form__first-name">
              <cdr-input
                v-model="firstName"
                name="firstName"
                label="First name"
                required
              />
              <div
                v-if="!isFirstNameValid && formSubmitted"
                class="modal-form__error-text"
              >
                <icon-x-fill
                  class="modal-form__error-icon"
                  aria-hidden="true"
                />
                <span class="notify-me-modal__sr-only">Error:</span>
                <cdr-text class="modal-form__error-description">
                  Please enter a valid first name
                </cdr-text>
              </div>
            </div>
            <div class="modal-form__last-name">
              <cdr-input
                v-model="lastName"
                name="lastName"
                label="Last name"
                required
              />
              <div
                v-if="!isLastNameValid && formSubmitted"
                class="modal-form__error-text"
              >
                <icon-x-fill
                  class="modal-form__error-icon"
                  aria-hidden="true"
                />
                <span class="notify-me-modal__sr-only">Error:</span>
                <cdr-text class="modal-form__error-description">
                  Please enter a valid last name
                </cdr-text>
              </div>
            </div>
          </div>
          <div class="modal-form__email">
            <cdr-input
              v-model="email"
              name="email"
              label="Email address"
              required
            />
            <div
              v-if="!isEmailValid && formSubmitted"
              class="modal-form__error-text"
            >
              <icon-x-fill
                class="modal-form__error-icon"
                aria-hidden="true"
              />
              <span class="notify-me-modal__sr-only">Error:</span>
              <cdr-text class="modal-form__error-description">
                Please enter a valid email
              </cdr-text>
            </div>
          </div>
          <input
            :value="eventData.sessionId"
            type="hidden"
            name="eventId"
          >
          <cdr-button
            v-if="status !== statuses.LOADING"
            full-width="@xs"
            data-ui="notify-me-submit-btn"
            class="modal-form__submit-btn"
            type="submit"
          >
            Notify me
          </cdr-button>
          <cdr-button
            v-if="status !== statuses.LOADING"
            modifier="secondary"
            full-width="@xs"
            data-ui="notify-me-no-thanks-btn"
            @click="closeModal"
          >
            No thanks
          </cdr-button>
          <img
            v-if="status === statuses.LOADING"
            src="https://satchel.rei.com/media/common/loader.gif"
            alt="Loading..."
          >
        </fieldset>
        <div
          v-if="status === statuses.ERROR"
          class="error-block"
        >
          <div class="error-block__text">
            <icon-x-fill
              class="modal-form__error-icon"
              aria-hidden="true"
            />
            <span class="notify-me-modal--sr-only">Error:</span>
            <span>There was a problem. Please check your information and try again.</span>
          </div>
          <cdr-list modifier="unordered">
            <span class="notify-me-modal--sr-only">Ways to get help</span>
            <li>
              <span>Call us at</span>
              <cdr-link
                href="tel:18004264840"
                modifier="standalone"
              >
                <span class="notify-me-modal--sr-only">Call us at</span>
                1-800-426-4840
              </cdr-link>
            </li>
            <li class="notify-me-modal--hidden-xs">
              <span>Get help in a</span>
              <cdr-link
                href="https://www.rei.com/help"
                modifier="standalone"
                data-ui="get-live-help"
              >
                <span class="notify-me-modal--sr-only">Get help in a </span>
                live chat
              </cdr-link>
            </li>
          </cdr-list>
        </div>
      </form>
    </div>
    <div v-if="status === statuses.COMPLETE">
      <cdr-text class="notify-me-modal__description">
        We'll let you know if a spot opens up for:
        <span class="notify-me-modal__sr-only">{{ eventData.title }}.</span>
      </cdr-text>
      <EventItem v-bind="eventData" />
      <cdr-button
        modifier="secondary"
        class="form__done-btn"
        data-ui="notify-me-done-btn"
        @click="closeModal"
      >
        Back to page
      </cdr-button>
    </div>
  </div>
</template>
